import { useParams, Link } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { PageHeader, Section } from '@/components/layout/AppShell'
import { Card, StatCard } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { DetailPageSkeleton } from '@/components/ui/Skeleton'
import { DateDisplay } from '@/components/shared/DateDisplay'
import { useCompany, useCompanyTenders } from '@/hooks/useCompanies'
import { formatTHB } from '@/lib/utils'

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>()

  const { data: company, isLoading: companyLoading } = useCompany(id!)
  const { data: tenderData, isLoading: tendersLoading } = useCompanyTenders(id!)

  if (companyLoading) {
    return <DetailPageSkeleton />
  }

  if (!company) {
    return (
      <Card className="p-8 text-center">
        <Icon icon="solar:buildings-2-line-duotone" className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="font-display text-xl font-semibold text-slate-900">ไม่พบบริษัท</h2>
        <p className="text-slate-500 mt-2">บริษัทนี้อาจถูกลบหรือไม่มีอยู่</p>
        <Link to="/companies">
          <Button className="mt-4">
            <Icon icon="solar:arrow-left-line-duotone" className="w-4 h-4 mr-2" />
            กลับไปหน้าบริษัท
          </Button>
        </Link>
      </Card>
    )
  }

  const initials = company.name
    ?.split(' ')
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?'

  const breadcrumbs = [
    { label: 'บริษัท', href: '/companies' },
    { label: company.name?.slice(0, 30) + '...' || 'บริษัท' },
  ]

  return (
    <div>
      <PageHeader
        title=""
        breadcrumbs={breadcrumbs}
        actions={
          <div className="flex items-center gap-2">
            <IconButton
              icon="solar:bookmark-line-duotone"
              label="บันทึก"
              variant="ghost"
            />
            <IconButton
              icon="solar:share-line-duotone"
              label="แชร์"
              variant="ghost"
            />
          </div>
        }
      />

      {/* Company header */}
      <Card className="mb-6">
        <div className="flex items-start gap-6">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center text-2xl font-bold">
            {initials}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-slate-900">
              {company.name}
            </h1>

            {company.tax_id && (
              <div className="flex items-center gap-2 mt-2 text-slate-600">
                <Icon icon="solar:card-line-duotone" className="w-5 h-5 text-slate-400" />
                <span>เลขประจำตัวผู้เสียภาษี: {company.tax_id}</span>
              </div>
            )}

            {company.address && (
              <div className="flex items-start gap-2 mt-2 text-slate-600">
                <Icon icon="solar:map-point-line-duotone" className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                <span>{company.address}</span>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Stats */}
      {tenderData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="เสนอราคาทั้งหมด"
            value={tenderData.totalBids}
            icon={<Icon icon="solar:hand-money-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="ชนะสัญญา"
            value={tenderData.totalWins}
            icon={<Icon icon="solar:medal-ribbon-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="อัตราการชนะ"
            value={tenderData.totalBids > 0
              ? `${((tenderData.totalWins / tenderData.totalBids) * 100).toFixed(0)}%`
              : '-'}
            icon={<Icon icon="solar:chart-line-duotone" className="w-5 h-5" />}
          />
          <StatCard
            title="มูลค่าสัญญารวม"
            value={formatTHB(tenderData.awards.reduce((sum, a) => sum + (a.total_price || 0), 0))}
            icon={<Icon icon="solar:wallet-line-duotone" className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Recent tenders */}
      <Section title="กิจกรรมล่าสุด" description="การประมูลที่บริษัทนี้เข้าร่วม">
        {tendersLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-4 w-3/4 bg-slate-200 rounded" />
                <div className="h-3 w-1/2 bg-slate-200 rounded mt-2" />
              </Card>
            ))}
          </div>
        ) : tenderData?.awards.length === 0 && tenderData?.bids.length === 0 ? (
          <Card className="p-8 text-center">
            <Icon icon="solar:inbox-line-duotone" className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900">ยังไม่มีกิจกรรม</h3>
            <p className="text-sm text-slate-500 mt-1">
              การเข้าร่วมประมูลของบริษัทนี้จะแสดงที่นี่
            </p>
          </Card>
        ) : (
          <div className="flex flex-col gap-3">
            {/* Awards */}
            {tenderData?.awards.map((award) => (
              <Link key={award.id} to={`/tenders/${award.tender_id}`}>
                <Card hover>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="success" size="sm">
                          <Icon icon="solar:medal-ribbon-line-duotone" className="w-3 h-3" />
                          ชนะ
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {award.award_date ? (
                            <DateDisplay date={award.award_date} format="relative" />
                          ) : (
                            '-'
                          )}
                        </span>
                      </div>
                      <p className="font-display font-medium text-slate-900 mt-1.5 line-clamp-2">
                        {(award.expand?.tender_id as any)?.project_name || 'Tender'}
                      </p>
                    </div>
                    {award.total_price && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">มูลค่าสัญญา</p>
                        <p className="font-medium text-slate-900">{formatTHB(award.total_price)}</p>
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}

            {/* Bids */}
            {tenderData?.bids.map((bid) => (
              <Link key={bid.id} to={`/tenders/${bid.tender_id}`}>
                <Card hover>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" size="sm">
                          <Icon icon="solar:hand-money-line-duotone" className="w-3 h-3" />
                          เสนอราคา
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {bid.submitted_at ? (
                            <DateDisplay date={bid.submitted_at} format="relative" />
                          ) : (
                            '-'
                          )}
                        </span>
                      </div>
                      <p className="font-display font-medium text-slate-900 mt-1.5 line-clamp-2">
                        {(bid.expand?.tender_id as any)?.project_name || 'Tender'}
                      </p>
                    </div>
                    {bid.bid_price && (
                      <div className="text-right">
                        <p className="text-xs text-slate-500">ราคาที่เสนอ</p>
                        <p className="font-medium text-slate-900">{formatTHB(bid.bid_price)}</p>
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  )
}
